import $ from 'zepto';
import { getXoom } from '../xoom';
import { $$$ } from '../../utils/legacy';
import initPopups from '../ui/Popups';
import initManager from '../ui/UIManager';

const initInlineErrors = () => {
  const xoom = getXoom();

  if (!xoom.ui.Popups) {
    initPopups();
  }

  if (!xoom.UIManager) {
    initManager();
  }

  const inlineErrPopups = {};
  const inlineErrors = {};
  const ERROR = 'error';

  /**
  Add an inline error
  @method add
  @param {String} popupId id to be assigned to popup
  @param {String} targetSelector jQuery selector of element which has the error
  @param {String} message Message which is to be displayed to the user
  @param {Object} opts Hash of xoom.ui.Popups options
  @private
  */
  function add(popupId, targetSelector, message, opts) {
    const calloutField = $(targetSelector);
    const val = calloutField.val();
    const options = $.extend({
      type: ERROR,
      position: 'bottom',
      bindTargetChangeEvent: true,
      message,
    }, opts);
    const errorPopup = xoom.ui.Popups.add(popupId, calloutField, options);

    calloutField.addClass(ERROR);
    errorPopup.show();

    if (options.bindTargetChangeEvent) {
      calloutField.bind('change keyup', () => {
        if (calloutField.val() !== val) {
          errorPopup.hide();
          calloutField.removeClass(ERROR);
        } else {
          errorPopup.show();
        }
      });
    }

    if (options.callback) {
      options.callback(errorPopup);
    }

    inlineErrPopups[popupId] = errorPopup;
  }

  /**
   Removes popup and error class
   @method remove
   @param {String} popupId unique ID for the tooltip/popup
   @private
   */
  function remove(popupId) {
    const popupInfo = inlineErrors[popupId];
    const popUp = inlineErrPopups[popupId];

    if (popupInfo) {
      $(popupInfo.targetSelector).removeClass(ERROR);
    }

    if (popUp) {
      popUp.hide();
    }
  }

  /**
   Inject an inline error and render when DOM is ready
   @method injectHighPriority
   @param {String} targetSelector selector to which the tooltip should be anchored
   @param {String} messageSelector messageContainer
   @param {String} messageType Message type (info/warn/error)
   @param {Function} callback
   @private
   */
  function injectHighPriority(targetSelector, messageSelector, messageType, callback) {
    const $element = $(messageSelector).hide();

    if ($element.length) {
      $(document).ready(() => {
        const $target = $(targetSelector);
        const text = $element.html();

        add(null, $target, text, {
          bindTargetChangeEvent: false,
          position: 'right',
          positionAllowOutsideViewport: 1,
          className: `xtip-high-priority xtip-type-${messageType}`,
          onShow: callback,
        });
      });
    }
  }

  /**
   Inject an inline error and render
   @method injectFieldLevelMessage
   @param {String} messageID message id
   @param {String} targetSelector selector to which the tooltip should be anchored
   @param {String} message message
   @param {String} messageType Message type (info/warn/error)
   @param {Object} [opts] Options
   @private
   */
  function InjectFieldLevelMessage(messageID, targetSelector, message, messageType, opts) {
    const defaultOptions = {
      scope: null, /* Optional: Scope/content of where the popup appears.  Useful when appearing in Ajax content */
      hideOnBodyClick: false,
    };
    const options = $.extend({}, defaultOptions, opts || {});
    const MESSAGE_TEMPLATE = '<div id="{0}" class="field-level-message form-message form-{1}">{2}</div>';
    const $boundField = $(options.boundFieldSelector);
    const boundFieldClass = options.boundFieldClass || '.xform-row';
    const customClasses = options.customMsgClasses;
    const $target = $(targetSelector);
    let $fieldMsg = $$$(messageID);
    const msgClass = options.msgClass || `has-${messageType}s`;
    const xvxMsgClass = `xvx-field--${messageType}`;

    function init() {
      let messageFinal = message;
      if (messageFinal && messageFinal.length) {
        if (messageFinal.indexOf('<p>') === -1) {
          // wrap message with <p> tag if none exists for correct semantics
          messageFinal = `<div class="input-message ${customClasses}">${messageFinal}</div>`;
        }

        // hide message initially, and show via animation
        $fieldMsg = $(MESSAGE_TEMPLATE.replaceTokens(messageID, messageType, messageFinal)).hide();

        // add field level error message
        $target.after($fieldMsg);

        // add error class to highlight the target element/input field
        if ($boundField.length) {
          $boundField.closest(boundFieldClass).addClass(msgClass);
        } else {
          $target.closest(boundFieldClass).addClass(msgClass);
          $target.find('.xvx-field:first').addClass(xvxMsgClass);
        }

        // then show via animation
        $fieldMsg.slideDown('normal', () => {
          if (options.callback) {
            options.callback();
          }
        });
      }
    }

    function removeFieldLevelMessage(callback) {
      $fieldMsg.slideUp('normal', () => {
        $fieldMsg.remove();
        if (callback) {
          callback();
        }
      });

      if ($boundField.length) {
        $boundField.closest(boundFieldClass).removeClass(msgClass);
      } else {
        $target.closest(boundFieldClass).removeClass(msgClass);
        $target.find('.xvx-field:first').removeClass(xvxMsgClass);
      }
    }

    init();

    return {
      remove: removeFieldLevelMessage,
      hide() {
        remove();
        if (options.closeCallBack) {
          options.closeCallBack();
          xoom.UIManager.unregister(messageID, options.scope);
        }
      },
    };
  }

  /**
   Add new instance of inline message
   @method addInlineMessage
   @param {String} messageID message id
   @param {String} targetSelector selector to which the tooltip should be anchored
   @param {String} message message
   @param {String} messageType Message type (info/warn/error)
   @param {Object} opts Options
   @private
   */
  function addInlineMessage(messageID, targetSelector, message, messageType, opts) {
    const TEMP_ELEMENT_ID = 'xoomtemp';
    let messageIDFinal = messageID;

    // create random id if no popupId was specified
    if (!messageIDFinal) {
      messageIDFinal = TEMP_ELEMENT_ID + xoom.random();
    }

    const inlineMessage = new InjectFieldLevelMessage(messageIDFinal, targetSelector, message, messageType, opts);

    if (opts) {
      xoom.UIManager.register(messageIDFinal, inlineMessage, opts.scope, opts.hideOnBodyClick);
    } else {
      xoom.UIManager.register(messageIDFinal, inlineMessage);
    }

    return inlineMessage;
  }

  /**
  Public Interface for this object
  @object
  @return {Object}
  @public
  */
  xoom.form.InlineErrors = {
    /**
      Add an inline error
      @method add
      @param {String} popupId id to be assigned to popup
      @param {String} targetSelector jQuery selector of element which has the error
      @param {String} message Message which is to be displayed to the user
      @param {Object} opts Hash of xoom.ui.Popups options
      @public
    */
    add(popupId, targetSelector, message, opts) {
      add(popupId, targetSelector, message, opts);
      inlineErrors[popupId] = { id: popupId, targetSelector };
    },

    /**
     Get the inline error object
      @method remove
      @param {String} popupId id to retrieve
      @public
      */
    remove,

    /**
     Get the inline error object
      @method get
      @param {String} popupId id to retrieve
      @public
      */
    get(popupId) { return inlineErrPopups[popupId]; },

    /**
     Add new instance of inline message
      @method addInlineMessage
      @param {String} messageID message id
      @param {String} targetSelector selector to which the tooltip should be anchored
      @param {String} message message
      @param {String} messageType Message type (info/warn/error)
      @param {Object} opts Options
      @public
      */
    addInlineMessage,

    /**
     Inject an inline error and render when DOM is ready
      @method injectHighPriority
      @param {String} targetSelector selector to which the tooltip should be anchored
      @param {String} messageSelector messageContainer
      @param {String} messageType Message type (info/warn/error)
      @public
      */
    injectHighPriority,

    /**
     Inject an inline error and render when DOM is ready
      @method injectFieldLevelMessage
      @param {String} messageID message id
      @param {String} targetSelector selector to which the tooltip should be anchored
      @param {String} message message
      @param {String} messageType Message type (info/warn/error)
      @public
      */
    InjectFieldLevelMessage,
  };
  return xoom;
};

export default initInlineErrors;
